import React from 'react';
import { ImageUploadBox } from './shared/ImageUploadBox';
import { FeaturedImageBox } from './shared/FeaturedImageBox';
import { DraggableImageBox } from './shared/DraggableImageBox';

interface ThumbnailSelectorProps {
  readonly productKey: string;
  readonly selectedImageUrl?: string | undefined;
  readonly candidateImageUrl?: string | undefined;
  readonly optionImages: string[];
  readonly fileInputRef: Record<string, HTMLInputElement | null>;
  readonly onThumbnailSelect: (productKey: string, imageUrl: string) => void;
  readonly onDragStart: (e: React.DragEvent, imageUrl: string, productKey: string) => void;
  readonly onDrop: (e: React.DragEvent, productKey: string) => void;
  readonly onDragOver: (e: React.DragEvent) => void;
  readonly onFileUpload: (event: React.ChangeEvent<HTMLInputElement>, productKey?: string) => void;
}

export const ThumbnailSelector = ({
  productKey,
  selectedImageUrl,
  candidateImageUrl,
  optionImages,
  fileInputRef,
  onThumbnailSelect,
  onDragStart,
  onDrop,
  onDragOver,
  onFileUpload,
}: ThumbnailSelectorProps) => {
  const [imageDimensions, setImageDimensions] = React.useState<Record<string, { width: number; height: number }>>({});

  const allImages = React.useMemo(() => {
    const images = [...optionImages];
    if (candidateImageUrl && !images.includes(candidateImageUrl)) {
      images.unshift(candidateImageUrl);
    }
    return images;
  }, [candidateImageUrl, optionImages]);

  React.useEffect(() => {
    const loadDimensions = async () => {
      const dimensions: Record<string, { width: number; height: number }> = {};

      const loadImage = (url: string): Promise<{ width: number; height: number }> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => resolve({ width: 0, height: 0 });
          img.src = url;
        });
      };

      if (selectedImageUrl) {
        dimensions[selectedImageUrl] = await loadImage(selectedImageUrl);
      }

      for (const url of allImages) {
        dimensions[url] = await loadImage(url);
      }

      setImageDimensions(dimensions);
    };

    loadDimensions();
  }, [selectedImageUrl, allImages]);

  const getImageDimension = (url: string | undefined) => {
    if (!url || !imageDimensions[url]) {
      return '';
    }
    const { width, height } = imageDimensions[url];
    return width > 0 && height > 0 ? `${width}×${height}` : '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '20px' }}>
        <div style={{ minWidth: '320px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ width: '150px' }}>
              <FeaturedImageBox
                imageUrl={selectedImageUrl}
                dimension={getImageDimension(selectedImageUrl)}
                productKey={productKey}
                onDrop={onDrop}
                onDragOver={onDragOver}
              />
            </div>
            <div style={{ width: '150px' }}>
              <ImageUploadBox
                inputKey={`featured-${productKey}`}
                fileInputRef={fileInputRef}
                onFileUpload={(e) => onFileUpload(e, productKey)}
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </div>

        {allImages.length > 0 && (
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: '12px',
              }}
            >
              {allImages.map((url, idx) => (
                <DraggableImageBox
                  key={`option-${idx}`}
                  url={url}
                  idx={idx}
                  isSelected={selectedImageUrl === url}
                  dimension={getImageDimension(url)}
                  align="left"
                  onClick={() => onThumbnailSelect(productKey, url)}
                  onDragStart={(e) => onDragStart(e, url, productKey)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
